'use client'

import { ArrowLeft, Search } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { Button } from '@/src/components/ui/button'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { cn } from '@/src/utils/styles'
import { useScrollDirection } from '../hooks/use-scroll-direction'
import type { User } from '../services/user'
import { AuthSection } from './navbar/auth-section'

/** Server passed props prevent hydration issues. */
interface NavbarProps {
  serverUser: User
}

import { useQuery } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import {
  useCurrentNavigationItem,
  useNavigationHistory,
} from '@/src/stores/navigation/actions'
import { getInsightQuery } from '../queries/keys/insights'
import type { NavigationItem } from '../stores/navigation/atoms'
import { isSamePage } from '../stores/navigation/utils'
import { useTrade } from '../stores/route-machine/actions'
import { SearchModalDialog } from './search-modal'

export function Navbar({ serverUser }: NavbarProps) {
  const navigationItem = useCurrentNavigationItem()
  const lastPushedNavigationPage = useRef<NavigationItem | undefined>(undefined)
  const { pushPath } = useNavigationHistory()
  const { goBack } = useTrade()
  const { slug } = useParams()

  const isRoutePage = navigationItem.path.startsWith('/route')
  const isInsightPage = navigationItem.path.startsWith('/insight')
  const showBackButton =
    navigationItem.path.startsWith('/tokens') ||
    navigationItem.path.startsWith('/insight') ||
    isRoutePage

  const scrollState = useScrollDirection(10, 3)
  const shouldShowNavbar = scrollState.isVisible

  const {
    close: closeSearchModal,
    isOpen: isSearchModalOpen,
    open: openSearchModal,
  } = useDisclosure()

  const { data: insight } = useQuery(
    getInsightQuery(slug as string, isInsightPage && slug !== undefined),
  )

  useEffect(() => {
    if (
      lastPushedNavigationPage.current === undefined ||
      !isSamePage(navigationItem, lastPushedNavigationPage.current)
    ) {
      pushPath(navigationItem)
      lastPushedNavigationPage.current = navigationItem
    }
  }, [navigationItem, pushPath])

  return (
    <nav
      className={cn(
        'sticky flex sm:grid grid-flow-col grid-cols-3 auto-cols-min gap-2 items-center justify-between h-navbar top-0 left-0 right-0 px-page-gutter z-10 bg-gradient-to-b from-background-80 to-transparent transition-all duration-500 ease-default',
        {
          'translate-y-0': shouldShowNavbar,
          '-translate-y-full': !shouldShowNavbar,
        },
      )}
    >
      <div className='flex items-center gap-2 md:gap-3 flex-grow sm:flex-grow-0'>
        <Link
          data-testid='polaris'
          href='/'
          className='px-1.5 py-0.5 sm:px-3 sm:py-2 sm:-ml-3 sm:-mr-1 sm:-my-2 focus-outer rounded-full hover:opacity-90'
        >
          <div
            className={cn(
              'block overflow-hidden rounded-full',
              showBackButton ? 'w-[21px] sm:w-6' : 'w-[94px] sm:w-[108px]',
            )}
          >
            <Image
              src='/assets/logo.svg'
              alt='Polaris logo'
              width={107}
              height={32}
              className='h-7 sm:h-8 w-auto max-w-none'
            />
          </div>
        </Link>
        {showBackButton && (
          <div className='bg-background-80 backdrop-blur-lg rounded-full flex'>
            <Button
              type='button'
              className='size-8 sm:size-12'
              size={{
                default: 'sm',
                sm: 'md',
              }}
              variant='secondary'
              onClick={goBack}
            >
              <ArrowLeft
                width={24}
                height={24}
                className='size-5 min-w-5 sm:size-6 sm:min-w-6 text-button-secondary-text'
              />
            </Button>
          </div>
        )}
      </div>
      <div className='flex items-center justify-center'>
        <div className='bg-background-80 backdrop-blur-lg rounded-full flex sm:grow sm:max-w-sm'>
          <Button
            variant='secondary'
            size={{
              default: 'sm',
              sm: 'md',
            }}
            onClick={openSearchModal}
            className='group/search w-8 sm:w-full'
          >
            <Search
              width={16}
              height={16}
              className='text-button-secondary-text size-4 min-w-4 min-h-4'
            />
            <span className='hidden sm:inline text-input-placeholder group-hover/search:text-inherit'>
              Search <span className='hidden sm:inline'>any token&hellip;</span>
            </span>
          </Button>
        </div>
        <SearchModalDialog
          isOpen={isSearchModalOpen}
          open={openSearchModal}
          close={closeSearchModal}
        />
      </div>
      <div className='flex items-center justify-end gap-2'>
        <AuthSection
          showBackButton={showBackButton}
          isRoutePage={isRoutePage}
          isInsightPage={isInsightPage}
          serverUser={serverUser}
          insight={insight}
        />
      </div>
    </nav>
  )
}
